/* notice-pdp.js — product-template behaviour, split out of custom.js so the
   other templates stop downloading ~40 KB of gallery / lightbox / waitlist code.
   Loaded deferred by theme.liquid on product templates only, after custom.js.
   Contents:
     - buyButtons(): toggles body.stick-buy-buttons when the buy buttons scroll away
     - variant-selects placement between the mobile and desktop pickers
     - notice gallery drift controller (.media-scroll-container / .notice-gallery-stack)
     - review "Read More" badge + review modal (Judge.me)
     - product media lightbox (#productMediaModal)
     - sold-out overlay + waitlist modal */

let _buyButtonsObserver = null;

function buyButtons() {
  const target = document.querySelector('.product--mobile-btn .track-btns__position')
    || document.querySelector('.track-btns__position');

  if (!target) return;
  if (_buyButtonsObserver) _buyButtonsObserver.disconnect();

  _buyButtonsObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && window.scrollY > 300) {
        document.body.classList.add('stick-buy-buttons');
      } else {
        document.body.classList.remove('stick-buy-buttons');
      }
    },
    {
      root: null,
      threshold: 0
    }
  );

  _buyButtonsObserver.observe(target);
}

// Dedupe VariantSelects change listener: moving the element between mobile/desktop
// containers via appendChild fires disconnectedCallback → connectedCallback, stacking
// change listeners because Shopify's connectedCallback calls addEventListener
// unconditionally. Per-instance flag guards re-attach; per-class sentinel makes the
// patch idempotent across repeated custom.js evaluations.
(function dedupeVariantSelectsListener() {
  var Cls = customElements.get('variant-selects');
  if (!Cls || Cls.prototype._placementGuarded) return;
  Cls.prototype._placementGuarded = true;
  var orig = Cls.prototype.connectedCallback;
  Cls.prototype.connectedCallback = function () {
    if (this._changeListenerAttached) return;
    this._changeListenerAttached = true;
    orig.call(this);
  };
  // Existing instances already had the original connectedCallback run before this
  // patch applied (global.js defer order), attaching one listener without setting
  // the flag. Mark them now so a subsequent move-triggered reconnect doesn't stack
  // a second listener via the patched path.
  document.querySelectorAll('variant-selects').forEach(function (el) {
    el._changeListenerAttached = true;
  });
})();

// Cached refs — populated on first successful lookup so resize doesn't re-query DOM
let _vpDesktopPicker = null;
let _vpMobileSource  = null;

function variantPlacement() {
  const desktopBreakpoint = 1024;
  const isDesktop = window.innerWidth >= desktopBreakpoint;

  if (!_vpDesktopPicker) _vpDesktopPicker = document.querySelector('.desktop-variant-picker');
  if (!_vpDesktopPicker) return;

  if (!_vpMobileSource) {
    _vpMobileSource = document.querySelector('.tyler-v2-swatches')
      || document.querySelector('.product__details--mob-title');
  }
  if (!_vpMobileSource) return;

  const desktopPicker = _vpDesktopPicker;
  const mobileSource  = _vpMobileSource;

  // Store original parent on first call
  const variantSelects = mobileSource.querySelector('variant-selects');
  if (!variantSelects) return;

  if (!variantSelects._originalParent) {
    variantSelects._originalParent = variantSelects.parentElement;
  }

  if (isDesktop) {
    if (variantSelects.parentElement !== desktopPicker) {
      desktopPicker.appendChild(variantSelects);
    }
  } else {
    if (variantSelects._originalParent && variantSelects.parentElement !== variantSelects._originalParent) {
      variantSelects._originalParent.appendChild(variantSelects);
    }
  }
}

let _variantPlacementTimer;
window.addEventListener('resize', function () {
  clearTimeout(_variantPlacementTimer);
  _variantPlacementTimer = setTimeout(variantPlacement, 150);
});

// After variant-linked product swap, product-info replaces the whole DOM subtree
// and the new <variant-selects> is rendered inside the mobile container (hidden on
// desktop). Re-run placement so the desktop picker isn't left empty. Same-product
// variant changes also trigger this event; in that case variantPlacement() no-ops
// (the element is already in the correct parent).
document.addEventListener('variant:change', function () {
  _vpDesktopPicker = null;
  _vpMobileSource  = null;
  requestAnimationFrame(function () {
    if (document.querySelector('.desktop-variant-picker')) variantPlacement();
  });
});

function noticeGalleryShouldUseNative() {
  try {
    if (window.matchMedia) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    }
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn) {
      if (conn.saveData) return true;
      var et = conn.effectiveType;
      if (et === 'slow-2g' || et === '2g') return true;
    }
    if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory < 1) return true;
  } catch (e) {}
  return false;
}

function noticeGalleryEmit(name, payload) {
  try {
    window.dataLayer = window.dataLayer || [];
    var evt = { event: 'pdp_gallery_' + name };
    if (payload) for (var k in payload) if (Object.prototype.hasOwnProperty.call(payload, k)) evt[k] = payload[k];
    window.dataLayer.push(evt);
  } catch (e) {}
}

function noticeGalleryParseSlot(alt) {
  if (!alt) return null;
  var m = String(alt).toLowerCase().match(/slot:([a-z0-9_\-]+)/);
  return m ? m[1] : null;
}

function noticeGalleryFindVariantIndex(container, variant) {
  if (!container || !variant) return -1;
  var slides = Array.prototype.slice.call(container.querySelectorAll('.product--media--main'));
  if (!slides.length) return -1;

  var variantSlot = variant.featured_media && variant.featured_media.alt
    ? noticeGalleryParseSlot(variant.featured_media.alt) : null;
  if (variantSlot) {
    for (var s = 0; s < slides.length; s++) {
      if (slides[s].getAttribute('data-media-slot') === variantSlot) return s;
    }
  }

  var mediaId = variant.featured_media && variant.featured_media.id;
  if (mediaId != null) {
    var mediaIdStr = String(mediaId);
    for (var i = 0; i < slides.length; i++) {
      if (slides[i].getAttribute('data-media-id') === mediaIdStr) return i;
    }
  }

  if (variant.id != null) {
    var idStr = String(variant.id);
    for (var j = 0; j < slides.length; j++) {
      var ids = (slides[j].getAttribute('data-variant-ids') || '').trim();
      if (ids && ids.split(/\s+/).indexOf(idStr) !== -1) return j;
    }
  }

  return -1;
}

function noticeGalleryScrollToVariant(variant) {
  if (!variant) return;
  var stack = document.querySelector('.notice-gallery-stack');
  var carousels = stack
    ? Array.prototype.slice.call(stack.querySelectorAll('.media-scroll-container'))
    : Array.prototype.slice.call(document.querySelectorAll('.media-scroll-container'));
  if (!carousels.length) return;

  // Multi-carousel mode: find the per-color carousel matching the variant
  var colorIdx = stack ? parseInt(stack.getAttribute('data-color-option-index'), 10) : -1;
  var targetCarousel = null;
  if (!isNaN(colorIdx) && colorIdx >= 0 && variant.options) {
    var color = variant.options[colorIdx];
    if (color) {
      var colorLower = String(color).toLowerCase().replace(/^\s+|\s+$/g, '');
      for (var i = 0; i < carousels.length; i++) {
        if ((carousels[i].getAttribute('data-color') || '') === colorLower) {
          targetCarousel = carousels[i];
          break;
        }
      }
    }
  }
  if (!targetCarousel) targetCarousel = carousels[0];

  // Cross-fade: flip data-is-current; CSS handles opacity transition
  for (var j = 0; j < carousels.length; j++) {
    var c = carousels[j];
    var isMatch = c === targetCarousel;
    c.setAttribute('data-is-current', isMatch ? 'true' : 'false');
    if (isMatch) {
      noticeHydrateCarouselImages(c);
      var ctrl = c._noticeGalleryController || noticeGalleryCreateController(c);
      if (ctrl && typeof ctrl.goToVariant === 'function') ctrl.goToVariant(variant);
    }
  }
}

/* Non-current per-color carousels render img with data-src/data-srcset (no src),
   so visibility:hidden stacks can't eager-fetch never-shown images at load.
   srcset must be set before src so the browser never fetches the bare-src candidate. */
function noticeHydrateCarouselImages(scope, lazy) {
  if (!scope) return;
  // Drift mode positions slides with transforms outside the clipped wrap, so Chrome's lazy-load never fetches
  // them (review 4 Nov 2026): a carousel being shown hydrates eagerly; native (scroll-snap) mode keeps lazy.
  var eager = !lazy && scope.getAttribute('data-notice-drift') !== 'native' && !(typeof noticeGalleryShouldUseNative === 'function' && noticeGalleryShouldUseNative());
  scope.querySelectorAll('img[data-src]').forEach(function (img) {
    if (eager) img.loading = 'eager';
    var ds = img.getAttribute('data-srcset');
    if (ds) { img.srcset = ds; img.removeAttribute('data-srcset'); }
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
  });
}

/* One slide, right now (review 4 Nov 2026): the slide the gallery lands on (initial variant, deep link, swipe)
   must not wait for the hero-first schedule. Idempotent. */
function noticeHydrateSlideNow(slide) {
  var img = slide && slide.querySelector ? slide.querySelector('img[data-src]') : null;
  if (!img) return;
  img.loading = 'eager'; img.setAttribute('fetchpriority', 'high');
  var ds = img.getAttribute('data-srcset'); if (ds) { img.srcset = ds; img.removeAttribute('data-srcset'); }
  img.src = img.getAttribute('data-src'); img.removeAttribute('data-src');
}

/* Hero first (3 Nov 2026): gallery images after the hero render with data-src, so the hero image has the whole
   link on slow networks; hydrate them once the hero has loaded, on the first gallery interaction, or after 6 s,
   whichever comes first. Only for the carousel whose hero has a real src (the visible one); hidden colour
   carousels keep hydrating after window load as before. Runs for both gallery modes (review 4 Nov 2026):
   drift mode hydrates eagerly because its slides sit translated outside the clipped wrap where Chrome's
   lazy-load never fetches them; native (scroll-snap) mode keeps loading=lazy, which works in normal flow.
   Two tiers so a swipe lands on a loaded slide: the two slides after the hero fetch first at normal priority,
   the rest follow at low priority once those two are in (or after 8 s). */
function noticeHeroFirstHydrate(container, slides, eager) {
  var heroImg = slides[0] && slides[0].querySelector('img');
  if (!heroImg || !heroImg.getAttribute('src')) return;
  if (!container.querySelector('img[data-src]')) return;
  var done = false;
  var hydrate = function (list, prio) {
    list.forEach(function (img) {
      var src = img.getAttribute('data-src'); if (!src) return;      // already hydrated elsewhere (idle pass, slide-now)
      if (eager) img.loading = 'eager';
      if (prio) img.setAttribute('fetchpriority', prio);
      var ds = img.getAttribute('data-srcset'); if (ds) { img.srcset = ds; img.removeAttribute('data-srcset'); }
      img.src = src; img.removeAttribute('data-src');
    });
  };
  var go = function () {
    if (done) return; done = true;
    var imgs = Array.prototype.slice.call(container.querySelectorAll('img.product--media[data-src]'));
    var near = imgs.slice(0, 2), rest = imgs.slice(2), restDone = false;
    var hydrateRest = function () { if (restDone) return; restDone = true; hydrate(rest, 'low'); };
    hydrate(near, 'auto');
    var pending = near.length;
    near.forEach(function (img) {
      var tick = function () { if (--pending <= 0) hydrateRest(); };
      if (img.complete && img.naturalWidth > 0) tick();
      else { img.addEventListener('load', tick, { once: true }); img.addEventListener('error', tick, { once: true }); }
    });
    if (!near.length) hydrateRest(); else setTimeout(hydrateRest, 8000);   // long fallback: on slow networks the rest must not start before the near slides are in
  };
  if (heroImg.complete && heroImg.naturalWidth > 0) { go(); return; }
  heroImg.addEventListener('load', go, { once: true });
  heroImg.addEventListener('error', go, { once: true });
  // capture phase: the drift controller stops propagation on its own pointer handlers
  ['pointerdown', 'touchstart', 'wheel', 'keydown', 'focusin'].forEach(function (ev) { container.addEventListener(ev, go, { once: true, passive: true, capture: true }); });
  setTimeout(go, 6000);
}

function noticeGalleryCreateController(container) {
  if (!container) return null;
  if (container._noticeGalleryController && typeof container._noticeGalleryController.destroy === 'function') {
    container._noticeGalleryController.destroy();
  }

  var slides = Array.prototype.slice.call(container.querySelectorAll('.product--media--main'));
  if (!slides.length) return null;

  // Pedestal-matched tunables — overridable via data-* on .media-scroll-container
  var AUTO_SPEED = parseFloat(container.getAttribute('data-drift-speed'));
  if (!isFinite(AUTO_SPEED) || AUTO_SPEED <= 0) {
    var _isMobileVp = !!(window.matchMedia && window.matchMedia('(max-width: 768px)').matches);
    AUTO_SPEED = _isMobileVp ? 0.3167 : 0.32; // px/frame → 19 mobile / 19.2 desktop @60fps
  }
  var DRAG_SPEED = parseFloat(container.getAttribute('data-drag-speed'));
  if (!isFinite(DRAG_SPEED) || DRAG_SPEED <= 0) DRAG_SPEED = 2.53;
  var WHEEL_SPEED = parseFloat(container.getAttribute('data-wheel-speed'));
  if (!isFinite(WHEEL_SPEED) || WHEEL_SPEED <= 0) WHEEL_SPEED = 1.5;
  var LERP = parseFloat(container.getAttribute('data-drift-lerp'));
  if (!isFinite(LERP) || LERP <= 0 || LERP > 1) LERP = 0.065;
  var DRAG_THRESHOLD = 12.65;
  var DIRECTION = -1;

  var listeners = [];
  var timers = [];
  var raf = null;
  var observer = null;
  var modalObserver = null;
  var slideObserver = null;
  var destroyed = false;
  // Native mode only for reduced-motion / slow conn / low memory.
  // Single-image carousels stay in drift mode but tick() naturally skips auto when totalStripWidth <= viewportWidth.
  var nativeMode = noticeGalleryShouldUseNative() || slides.length < 1;
  var booted = false;
  var pendingIndex = null;

  // Pedestal one-offset model
  var itemsCache = [];     // [{ el, left, width }]
  var totalStripWidth = 0;
  var itemWidth = 0;
  var viewportWidth = 0;
  var currentDragX = 0;
  var targetDragX = 0;

  // State flags — drift pauses on any of these
  var isDragging = false;
  var isScaled = false;    // modal/zoom open
  var isHidden = false;    // tab hidden
  var isOffscreen = false; // gallery scrolled out of viewport
  var currentIdx = 0;

  // Drag tracking
  var dragStartX = 0;
  var dragLastX = 0;
  var dragMovedX = 0;
  var isClickIntent = true;
  var isMouseDown = false;
  var touchAxis = null;
  var touchStartX = 0;
  var touchStartY = 0;
  var resizeTimer = null;

  function on(el, type, handler, options) {
    el.addEventListener(type, handler, options);
    listeners.push({ el: el, type: type, handler: handler, options: options });
  }

  function setTimer(fn, delay) {
    var id = setTimeout(fn, delay);
    timers.push(id);
    return id;
  }

  function clearInlineLayout() {
    delete container.dataset.noticeDrift;
    container.style.height = '';
    container.style.overflow = '';
    container.style.scrollSnapType = '';
    container.style.position = '';
    container.style.cursor = '';
    container.style.userSelect = '';
    container.style.touchAction = '';
    slides.forEach(function (slide) {
      slide.style.position = '';
      slide.style.left = '';
      slide.style.top = '';
      slide.style.willChange = '';
      slide.style.transform = '';
    });
  }

  function nativeScrollToIndex(idx, smooth) {
    if (idx < 0 || idx >= slides.length) return;
    var target = slides[idx];
    container.scrollTo({
      left: target.offsetLeft,
      behavior: smooth === false ? 'auto' : 'smooth'
    });
  }

  // Center given index — Pedestal centering: targetDragX = -(left - viewportW/2 + width/2)
  function centerIndex(idx) {
    if (!itemsCache[idx]) return;
    var n = itemsCache[idx];
    targetDragX = -(n.left - viewportWidth / 2 + n.width / 2);
  }

  function goToIndex(idx, smooth) {
    if (idx < 0 || idx >= slides.length) return;
    noticeHydrateSlideNow(slides[idx]);                               // the landing slide never waits for the hero-first schedule
    if (!nativeMode && !booted) { pendingIndex = idx; return; }
    if (nativeMode) { nativeScrollToIndex(idx, smooth); return; }

    currentIdx = idx;
    centerIndex(idx);
    noticeGalleryEmit('media_selected', {
      index: idx,
      mediaId: slides[idx].getAttribute('data-media-id') || '',
      slot: slides[idx].getAttribute('data-media-slot') || ''
    });
  }

  function goToVariant(variant) {
    var idx = noticeGalleryFindVariantIndex(container, variant);
    if (idx >= 0) goToIndex(idx);
    noticeGalleryEmit('variant_media_changed', {
      variantId: (variant && variant.id) || null,
      index: idx
    });
  }

  function syncInitialVariant() {
    var initialVariantId = container.getAttribute('data-initial-variant-id');
    if (initialVariantId) goToVariant({ id: initialVariantId });
  }

  var controller = {
    destroy: destroy,
    goToIndex: goToIndex,
    goToVariant: goToVariant,
    pauseFor: function (reason) { if (reason === 'modal') isScaled = true; },
    resumeFrom: function (reason) { if (reason === 'modal') isScaled = false; },
    next: function () { goToIndex((currentIdx + 1) % slides.length); },
    prev: function () { goToIndex((currentIdx - 1 + slides.length) % slides.length); }
  };

  container._noticeGalleryController = controller;
  container._noticeGoTo = goToIndex;

  if (nativeMode) {
    clearInlineLayout();
    container.dataset.noticeDrift = 'native';
    noticeHeroFirstHydrate(container, slides, false);                // review 4 Nov 2026: native mode used to skip this entirely
    syncInitialVariant();
    wireResize();
    wireControlButtons();
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      noticeGalleryEmit('reduced_motion_disabled', {});
    }
    noticeGalleryEmit('loaded', { slides: slides.length, mode: 'native' });
    return controller;
  }

  waitForLayout();
  return controller;

  function waitForLayout() {
    var tries = 0;
    function ready() {
      var r = slides[0].getBoundingClientRect();
      return r.width >= 100 && r.height >= 100;
    }
    function attempt() {
      if (destroyed || booted) return true;
      if (!ready()) return false;
      initDrift();
      return true;
    }
    if (attempt()) return;
    if (document.readyState !== 'complete') {
      on(window, 'load', function () { if (!attempt()) poll(); }, { once: true });
    }
    poll();
    function poll() {
      setTimer(function retry() {
        tries++;
        if (attempt() || tries > 60) {
          return;
        }
        setTimer(retry, 100);
      }, 100);
    }
  }

  function measure() {
    var containerRect = container.getBoundingClientRect();
    viewportWidth = containerRect.width;
    var slideRects = slides.map(function (slide) { return slide.getBoundingClientRect(); });
    itemWidth = slideRects[0].width;
    // Slides are positioned absolute at left:0 by JS — adjacent-slide-rect math fails.
    // 4px mobile; desktop widened to 12px so the gutter actually reads between slides.
    // Must stay in sync with the desktop box-shadow separator in custom-product-new.css.
    var gap = (window.matchMedia && window.matchMedia('(min-width: 990px)').matches) ? 12 : 4;
    var step = itemWidth + gap;
    itemsCache = slides.map(function (slide, i) {
      return { el: slide, left: i * step, width: itemWidth };
    });
    totalStripWidth = step * slides.length;
  }

  function initDrift() {
    if (destroyed || booted) return;
    booted = true;

    clearInlineLayout();
    container.scrollLeft = 0;

    container.dataset.noticeDrift = 'active';
    // NOTE: position/height/inset come from CSS (stack-fade rules) — do not override here
    container.style.overflow = 'visible';
    container.style.scrollSnapType = 'none';
    container.style.cursor = 'grab';
    container.style.userSelect = 'none';
    container.style.touchAction = 'pan-y';

    slides.forEach(function (slide) {
      slide.style.position = 'absolute';
      slide.style.left = '0';
      // top set by CSS (top-1 mob / top-2 desktop)
      slide.style.willChange = 'transform';
      // Cached images don't fire onload — sweep for already-complete imgs
      var img = slide.querySelector('img');
      if (img && img.complete && img.naturalWidth > 0) img.classList.add('is-loaded');
    });
    noticeHeroFirstHydrate(container, slides, true);

    measure();
    render(true);
    wireDriftEvents();
    wireResize();
    wireControlButtons();
    wireKeyboard();
    wireModalSync();

    if (pendingIndex !== null) {
      goToIndex(pendingIndex);
      pendingIndex = null;
    } else {
      syncInitialVariant();
    }

    noticeGalleryEmit('loaded', { slides: slides.length, mode: 'drift' });
    noticeGalleryEmit('drift_started', { speed: AUTO_SPEED });
    raf = requestAnimationFrame(tick);
  }

  // Pedestal modulo wrap: s = (currentDragX + n.left + itemWidth) % total - itemWidth
  // Each frame writes only the slides that can be on screen. Every inline transform write costs a
  // style recalc, and writing all ~19 slides every frame kept the PDP main thread ~80% busy on a fast
  // laptop (8-10 ms of style recalc per frame; stacked long tasks on phones). The modulo keeps
  // s >= -itemWidth, so a slide can only leave the visible band on the right: it is written on the
  // frame it leaves (landing at s >= hi, past the clipped edge) and then left alone until it comes
  // back. The band reaches one slide past the right edge so small container width changes stay covered.
  // Full renders (boot, remeasure) write every slide.
  function render(full) {
    if (!totalStripWidth) return;
    var hi = viewportWidth + itemWidth;
    for (var i = 0; i < itemsCache.length; i++) {
      var n = itemsCache[i];
      var s = ((currentDragX + n.left + itemWidth) % totalStripWidth + totalStripWidth) % totalStripWidth - itemWidth;
      var inBand = s < hi;
      if (full || inBand || n.inBand) n.el.style.transform = 'translate3d(' + s + 'px, 0, 0)';
      n.inBand = inBand;
    }
  }

  function tick() {
    if (destroyed) return;
    // Skip work entirely on inactive (cross-faded out) carousels
    var isCurrent = container.getAttribute('data-is-current') !== 'false';
    if (!isCurrent) {
      raf = requestAnimationFrame(tick);
      return;
    }
    var changed = false;
    if (!isDragging && !isScaled && !isHidden && !isOffscreen) {
      if (totalStripWidth > viewportWidth) {
        var step = AUTO_SPEED * DIRECTION;
        currentDragX += step;
        targetDragX += step;
        changed = true;
      }
    }
    var diff = targetDragX - currentDragX;
    if (Math.abs(diff) > 0.01) {
      currentDragX += diff * LERP;
      changed = true;
    }
    if (changed) render();
    raf = requestAnimationFrame(tick);
  }

  function wireDriftEvents() {
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(function (entries) {
        isOffscreen = !entries[0].isIntersecting;
      }, { threshold: 0 });
      observer.observe(container);
    }

    on(document, 'visibilitychange', function () {
      isHidden = !!document.hidden;
    });

    // Horizontal wheel / trackpad — let vertical scroll pass through
    on(container, 'wheel', function (e) {
      if (isScaled) return;
      var absX = Math.abs(e.deltaX);
      var absY = Math.abs(e.deltaY);
      if (absX < 2 || absX <= absY * 1.15) return;
      e.preventDefault();
      targetDragX += e.deltaX * WHEEL_SPEED * DIRECTION;
    }, { passive: false });

    // Click-vs-drag — suppress click after a real drag
    on(container, 'click', function (e) {
      if (!isClickIntent) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Mouse drag
    on(container, 'mousedown', function (e) {
      if (e.button !== 0 || isScaled) return;
      isMouseDown = true;
      isDragging = true;
      isClickIntent = true;
      dragStartX = dragLastX = e.clientX;
      dragMovedX = 0;
      noticeGalleryEmit('drag_started', { input: 'mouse' });
      container.style.cursor = 'grabbing';
    });

    on(window, 'mousemove', function (e) {
      if (!isMouseDown) return;
      var delta = e.clientX - dragLastX;
      dragLastX = e.clientX;
      dragMovedX += Math.abs(delta);
      if (dragMovedX > DRAG_THRESHOLD) isClickIntent = false;
      targetDragX += delta * DRAG_SPEED;
    });

    on(window, 'mouseup', function () {
      if (!isMouseDown) return;
      isMouseDown = false;
      isDragging = false;
      container.style.cursor = 'grab';
      noticeGalleryEmit('drag_completed', { input: 'mouse', distance: dragMovedX });
    });

    // Touch drag with axis lock — vertical scroll wins
    on(container, 'touchstart', function (e) {
      if (!e.touches.length || isScaled) return;
      isClickIntent = true;
      dragMovedX = 0;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      dragLastX = touchStartX;
      touchAxis = null;
    }, { passive: true });

    on(container, 'touchmove', function (e) {
      if (!e.touches.length || isScaled) return;
      var x = e.touches[0].clientX;
      var y = e.touches[0].clientY;
      var totalDx = x - touchStartX;
      var totalDy = y - touchStartY;

      if (touchAxis === null && (Math.abs(totalDx) > 8 || Math.abs(totalDy) > 8)) {
        touchAxis = Math.abs(totalDx) > Math.abs(totalDy) * 1.15 ? 'x' : 'y';
        if (touchAxis === 'x') {
          isDragging = true;
          noticeGalleryEmit('drag_started', { input: 'touch' });
        }
      }

      if (touchAxis === 'x') {
        e.preventDefault();
        var delta = x - dragLastX;
        dragLastX = x;
        dragMovedX += Math.abs(delta);
        if (dragMovedX > DRAG_THRESHOLD) isClickIntent = false;
        targetDragX += delta * DRAG_SPEED;
      }
    }, { passive: false });

    on(container, 'touchend', function () {
      if (touchAxis === 'x') {
        noticeGalleryEmit('drag_completed', { input: 'touch', distance: dragMovedX });
      }
      isDragging = false;
      touchAxis = null;
    }, { passive: true });
  }

  function wireControlButtons() {
    var wrap = container.parentElement;
    if (!wrap) return;
    var prev = wrap.querySelector('[data-notice-gallery-prev]');
    var next = wrap.querySelector('[data-notice-gallery-next]');
    if (prev) on(prev, 'click', function () {
      controller.prev();
      noticeGalleryEmit('arrow_clicked', { direction: 'prev' });
    });
    if (next) on(next, 'click', function () {
      controller.next();
      noticeGalleryEmit('arrow_clicked', { direction: 'next' });
    });
  }

  function wireKeyboard() {
    if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '0');
    container.setAttribute('role', 'region');
    container.setAttribute('aria-roledescription', 'carousel');
    on(container, 'keydown', function (e) {
      if (e.key === 'ArrowLeft') { controller.prev(); e.preventDefault(); }
      else if (e.key === 'ArrowRight') { controller.next(); e.preventDefault(); }
      else if (e.key === 'Home') { goToIndex(0); e.preventDefault(); }
      else if (e.key === 'End') { goToIndex(slides.length - 1); e.preventDefault(); }
    });
  }

  function wireModalSync() {
    var modal = document.getElementById('productMediaModal');
    if (!modal || typeof MutationObserver !== 'function') return;
    modalObserver = new MutationObserver(function () {
      if (modal.classList.contains('open')) {
        isScaled = true;
        modal.setAttribute('aria-hidden', 'false');
        noticeGalleryEmit('lightbox_opened', {});
      } else {
        isScaled = false;
        modal.setAttribute('aria-hidden', 'true');
      }
    });
    modalObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
  }

  function remeasureKeepingPosition() {
    var oldTotal = totalStripWidth;
    var ratio = oldTotal ? currentDragX / oldTotal : 0;
    measure();
    currentDragX = ratio * totalStripWidth;
    targetDragX = currentDragX;
    render(true);
  }

  function wireResize() {
    var lastResizeWidth = window.innerWidth;
    on(window, 'resize', function () {
      // Mobile address-bar show/hide triggers resize without width change — ignore
      var w = window.innerWidth;
      if (w === lastResizeWidth) return;
      lastResizeWidth = w;
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (booted) {
          remeasureKeepingPosition();
        } else {
          noticeGalleryAutoplay();
        }
      }, 150);
    });

    // Slides are height-driven (aspect-ratio) on desktop: the wrap can settle
    // to its final height AFTER boot (svh/late CSS), growing slides without any
    // window resize. A stale itemWidth then makes step < slide width and slides
    // overlap — the "no gap between images" bug. Watch the slide box itself.
    if (typeof ResizeObserver !== 'undefined') {
      slideObserver = new ResizeObserver(function () {
        if (!booted || destroyed) return;
        var w = slides[0].getBoundingClientRect().width;
        if (Math.abs(w - itemWidth) < 1) return;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(remeasureKeepingPosition, 100);
      });
      slideObserver.observe(slides[0]);
    }
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    if (raf) cancelAnimationFrame(raf);
    if (observer) observer.disconnect();
    if (modalObserver) modalObserver.disconnect();
    if (slideObserver) slideObserver.disconnect();
    listeners.forEach(function (item) {
      try { item.el.removeEventListener(item.type, item.handler, item.options); } catch (e) {}
    });
    timers.forEach(function (id) { clearTimeout(id); });
    clearTimeout(resizeTimer);
    clearInlineLayout();
    if (container._noticeGalleryController === controller) container._noticeGalleryController = null;
    if (container._noticeGoTo === goToIndex) container._noticeGoTo = null;
  }
}

function noticeGalleryAutoplay() {
  // Safety net: every stack must have exactly one current carousel; if Liquid match failed, activate first.
  document.querySelectorAll('.notice-gallery-stack').forEach(function (stack) {
    var carousels = stack.querySelectorAll('.media-scroll-container');
    if (!carousels.length) return;
    var hasCurrent = false;
    for (var i = 0; i < carousels.length; i++) {
      if (carousels[i].getAttribute('data-is-current') === 'true') { hasCurrent = true; break; }
    }
    if (!hasCurrent) {
      carousels[0].setAttribute('data-is-current', 'true');       // its hero has a src; hero-first hydrates the rest (review 4 Nov 2026)
    }
  });
  document.querySelectorAll('.media-scroll-container').forEach(function (container) {
    noticeGalleryCreateController(container);
  });
  // After load, hydrate remaining hidden carousels off the critical path so
  // color switches stay instant (images land cached before the user switches).
  var noticeIdleHydrate = function () {
    if (typeof noticeGalleryShouldUseNative === 'function' && noticeGalleryShouldUseNative()) return;   // Save-Data / 2G / low memory: hidden colours hydrate on the swatch tap instead (review 4 Nov 2026)
    var hydrateAll = function () {
      document.querySelectorAll('.media-scroll-container:not([data-is-current="true"]) img[data-src]').forEach(function (img) {   // the visible carousel is hero-first's job
        var ds = img.getAttribute('data-srcset');
        if (ds) { img.srcset = ds; img.removeAttribute('data-srcset'); }
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
      });
    };
    if ('requestIdleCallback' in window) { requestIdleCallback(hydrateAll, { timeout: 4000 }); }
    else { setTimeout(hydrateAll, 1500); }
  };
  if (document.readyState === 'complete') { noticeIdleHydrate(); }
  else { window.addEventListener('load', noticeIdleHydrate, { once: true }); }
}

// Theme editor / Section Rendering API (review 4 Nov 2026): a re-rendered product section arrives with data-src
// gallery images and no controller; boot the gallery again for that section.
document.addEventListener('shopify:section:load', function (e) {
  if (e && e.target && e.target.querySelector && e.target.querySelector('.media-scroll-container')) noticeGalleryAutoplay();
});

document.addEventListener('DOMContentLoaded', function () {
  // Template-specific init — skip when the elements are absent
  if (document.querySelector('.desktop-variant-picker')) variantPlacement();
  if (document.querySelector('.track-btns__position')) buyButtons();
  if (document.querySelector('.media-scroll-container')) {
    noticeGalleryAutoplay();
    if (!window.__noticeGalleryVariantWired) {
      window.__noticeGalleryVariantWired = true;
      document.addEventListener('variant:change', function (e) {
        var v = e.detail && e.detail.variant;
        if (v && v.id != null) noticeGalleryScrollToVariant(v);
      });
    }
  }

  // Review "Read More" badge injection
  (window.requestIdleCallback || function(cb) { setTimeout(cb, 200); })(function () {
    document.querySelectorAll('.jdgm-prev-badge').forEach(function (el) {
      if (!el.nextElementSibling || !el.nextElementSibling.classList.contains('review-read_more')) {
        var span = document.createElement('span');
        span.className = 'review-read_more';
        span.textContent = 'Read More';
        el.parentNode.insertBefore(span, el.nextSibling);
      }
    });
  });

  // Review modal open / close — single delegated handler
  var _reviewWrappers = document.querySelectorAll('.product_review-wrapper');
  var _reviewMains    = document.querySelectorAll('.product_review--main');
  document.addEventListener('click', function (e) {
    if (e.target.closest('.review_close')) {
      _reviewWrappers.forEach(function (el) { el.style.display = 'none'; });
      _reviewMains.forEach(function (el) { el.classList.remove('active'); });
      document.body.classList.remove('overflow-hidden');
    } else if (e.target.closest('.review-read_more')) {
      if (!_reviewWrappers.length) {
        // new-template PDPs have no review modal: go to the Judge.me widget instead of locking scroll.
        var reviews = document.getElementById('judgeme_product_reviews') ||
          document.querySelector('.jdgm-review-widget, .jdgm-rev-widg, .jdgm-widget[data-id], [id*="judgeme_product_reviews"]');
        if (reviews) reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      _reviewWrappers.forEach(function (el) { el.style.display = ''; });
      _reviewMains.forEach(function (el) { el.classList.add('active'); });
      document.body.classList.add('overflow-hidden');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });

  /* Opening Act Fix - Issue #P04 */
  var modal = document.getElementById('productMediaModal');
  if (modal) {
    var gallery = document.getElementById('modalImageContainer');
    var closeModal = document.getElementById('closeModal');
    var prevArrow = modal.querySelector('.prev-arrow');
    var nextArrow = modal.querySelector('.next-arrow');
    var imageIndexText = modal.querySelector('.image-index');
    if (closeModal && prevArrow && nextArrow && imageIndexText && gallery) {
      var currentIndex = 0;
      var startX = 0;
      var imagesLoaded = false;

      // Recompute visible modal slides each time — set changes on variant:change.
      function getVisibleSlides() {
        return Array.prototype.filter.call(
          modal.querySelectorAll('.modal-slide'),
          function (s) { return s.getAttribute('data-hide-variant') !== 'true'; }
        );
      }

      function loadModalImages() {
        if (imagesLoaded) return;
        imagesLoaded = true;
        // Load all slides' images once; visible/hidden is controlled by CSS.
        modal.querySelectorAll('.modal-slide').forEach(function (slide) {
          if (slide.querySelector('img')) return;
          var img = document.createElement('img');
          img.src = slide.dataset.src;
          img.srcset = slide.dataset.srcset;
          img.sizes = '(min-width: 990px) 90vw, 100vw';
          img.alt = slide.dataset.alt || '';
          img.className = 'image-modal-img';
          img.loading = 'eager';
          slide.appendChild(img);
        });
      }

      function updateSlider(index) {
        var total = getVisibleSlides().length || 1;
        if (index < 0) index = 0;
        if (index > total - 1) index = total - 1;
        currentIndex = index;
        gallery.style.transform = 'translateX(-' + (index * 100) + '%)';
        imageIndexText.textContent = (index + 1) + ' / ' + total;
      }

      var _modalOpener = null;
      function closeLightbox() {
        modal.classList.remove('open');
        // Only release the page scroll lock if the cart drawer isn't also holding it.
        var cd = document.querySelector('cart-drawer');
        if (!(cd && cd.classList.contains('active'))) {
          document.body.classList.remove('overflow-hidden');
        }
        if (_modalOpener && _modalOpener.focus) { _modalOpener.focus(); _modalOpener = null; }
      }

      // Delegate clicks on any current/future gallery button (scroll container gets cloned on variant:change).
      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('.gallery-image-button');
        if (!btn) return;
        var slideEl = btn.closest('.product--media--main');
        if (!slideEl) return;
        var mediaId = slideEl.getAttribute('data-media-id');
        loadModalImages();
        var visible = getVisibleSlides();
        var idx = 0;
        if (mediaId) {
          for (var i = 0; i < visible.length; i++) {
            if (visible[i].getAttribute('data-media-id') === mediaId) { idx = i; break; }
          }
        }
        _modalOpener = btn;
        modal.classList.add('open');
        updateSlider(idx);
        document.body.classList.add('overflow-hidden');
        if (closeModal && closeModal.focus) closeModal.focus();
      });

      closeModal.addEventListener('click', closeLightbox);

      modal.addEventListener('click', function (e) {
        var isImage = e.target.classList.contains('image-modal-img');
        var isNav = e.target.closest('.image-modal-nav');
        if (!isImage && !isNav) {
          closeLightbox();
        }
      });

      prevArrow.addEventListener('click', function () {
        if (currentIndex > 0) updateSlider(currentIndex - 1);
      });

      nextArrow.addEventListener('click', function () {
        var total = getVisibleSlides().length;
        if (currentIndex < total - 1) updateSlider(currentIndex + 1);
      });

      gallery.addEventListener('touchstart', function (e) {
        startX = e.touches[0].clientX;
      }, { passive: true });

      gallery.addEventListener('touchend', function (e) {
        var deltaX = e.changedTouches[0].clientX - startX;
        var total = getVisibleSlides().length;
        if (deltaX > 50 && currentIndex > 0) updateSlider(currentIndex - 1);
        else if (deltaX < -50 && currentIndex < total - 1) updateSlider(currentIndex + 1);
      }, { passive: true });

      document.addEventListener('keydown', function (e) {
        if (!modal.classList.contains('open')) return;
        var total = getVisibleSlides().length;
        if (e.key === 'ArrowLeft' && currentIndex > 0) updateSlider(currentIndex - 1);
        else if (e.key === 'ArrowRight' && currentIndex < total - 1) updateSlider(currentIndex + 1);
        else if (e.key === 'Escape') {
          closeLightbox();
        } else if (e.key === 'Tab') {
          var f = modal.querySelectorAll('button, [tabindex]:not([tabindex="-1"])');
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
          else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        }
      });

      updateSlider(0);
    }
  }
});

/* ============================================================
   NOTICE — Sold-Out Overlay + Waitlist Modal
   ============================================================ */
(function () {
  var REPEAT = 24;

  function getBandText(overlay) {
    // Read from data attribute (set by Liquid from section settings)
    return (overlay && overlay.dataset.bandText) || 'ALLOCATION EXHAUSTED';
  }

  function buildTrackText(bandText) {
    var seg = Array(REPEAT).fill(bandText).join('\u00A0\u00A0\u00A0\u2014\u00A0\u00A0\u00A0');
    return seg + '\u00A0\u00A0\u00A0\u2014\u00A0\u00A0\u00A0' + seg;
  }

  function fillTrack(overlay) {
    var track = overlay.querySelector('.notice-soldout-track');
    if (track && !track.dataset.built) {
      track.textContent = buildTrackText(getBandText(overlay));
      track.dataset.built = '1';
    }
  }

  function setOverlayState(active) {
    document.querySelectorAll('.notice-soldout-overlay').forEach(function (overlay) {
      fillTrack(overlay);
      if (active) {
        overlay.classList.add('is-active');
        overlay.setAttribute('aria-hidden', 'false');
      } else {
        overlay.classList.remove('is-active');
        overlay.setAttribute('aria-hidden', 'true');
      }
    });
  }

  function initOverlay() {
    // Works for both media-gallery (main-product) and .notice-gallery-wrap (new-main-product)
    var container = document.querySelector('media-gallery[data-product-available], .notice-gallery-wrap[data-product-available]');
    if (!container) return;

    document.querySelectorAll('.notice-soldout-overlay').forEach(fillTrack);

    if (container.dataset.productAvailable === 'false') {
      setOverlayState(true);
    }
  }

  function bindVariantChange() {
    // Dawn uses a custom 'variant:change' event via pubsub
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      setOverlayState(!variant || !variant.available);
    });

    // Some themes use this form
    document.addEventListener('variantChange', function (e) {
      var variant = e.detail && (e.detail.variant || e.detail.selectedVariant);
      setOverlayState(!variant || !variant.available);
    });

    // Watch for Dawn's native variant input changes as fallback
    document.addEventListener('change', function (e) {
      var input = e.target;
      if (!input || input.name !== 'id') return;
      var form = input.closest('[data-type="add-to-cart-form"]');
      if (!form) return;
      // Check if the selected variant button has sold-out state
      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        setOverlayState(submitBtn.disabled || submitBtn.getAttribute('aria-disabled') === 'true');
      }
    });
  }

  /* ---- Waitlist Modal (JS-injected, no Liquid dependency) ---- */
  function getOrCreateModal() {
    var existing = document.getElementById('notice-waitlist-modal');
    if (existing) return existing;

    // Read product title from the page for the modal header
    var titleEl = document.querySelector('.product__title, h1.product-title, [class*="product__title"], h1');
    var productName = titleEl ? titleEl.textContent.trim().toUpperCase() : 'THIS PRODUCT';

    var modal = document.createElement('div');
    modal.id = 'notice-waitlist-modal';
    modal.className = 'notice-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Register interest');
    modal.innerHTML = [
      '<div class="notice-modal__inner">',
        '<button class="notice-modal__close" aria-label="Close">&#215;</button>',
        '<p class="notice-modal__status">PRODUCT: ' + productName + '</p>',
        '<p class="notice-modal__status">STATUS: ALLOCATION EXHAUSTED</p>',
        '<p class="notice-modal__status">ACTION: REGISTER FOR NEXT CYCLE</p>',
        '<hr class="notice-modal__divider">',
        '<form class="notice-modal__form" novalidate>',
          '<label for="notice-modal-email" class="visually-hidden">Email address</label>',
          '<input id="notice-modal-email" class="notice-modal__input" type="email" placeholder="EMAIL ADDRESS" required autocomplete="email" maxlength="120">',
          '<button class="notice-modal__submit" type="submit">SUBMIT REQUEST</button>',
          '<p class="notice-modal__error" role="alert" style="display:none;color:#c0392b;font-size:0.72rem;letter-spacing:0.04em;margin-top:0.5rem;"></p>',
          '<p class="notice-modal__consent" style="opacity:0.6;font-size:0.66rem;letter-spacing:0.04em;margin-top:0.5rem;">You\u2019ll receive a one-time back-in-stock notification for this product.</p>',
        '</form>',
        '<p class="notice-modal__confirm">REQUEST RECEIVED \u2014 WE WILL NOTIFY YOU.</p>',
      '</div>'
    ].join('');

    document.body.appendChild(modal);

    // Form submit
    var form = modal.querySelector('.notice-modal__form');
    var confirmMsg = modal.querySelector('.notice-modal__confirm');
    var errEl = modal.querySelector('.notice-modal__error');
    var submitBtn = form.querySelector('.notice-modal__submit');
    function showWaitlistError(msg) {
      if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var emailInput = form.querySelector('.notice-modal__input');
      var email = emailInput ? emailInput.value.trim() : '';
      if (errEl) errEl.style.display = 'none';

      // Visible validation (was a silent `return`).
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showWaitlistError('Please enter a valid email address.');
        if (emailInput) emailInput.focus();
        return;
      }
      // In-flight guard: block duplicate POSTs / duplicate Lead events from rapid resubmits.
      if (submitBtn && submitBtn.disabled) return;
      if (submitBtn) { submitBtn.disabled = true; submitBtn.setAttribute('aria-busy', 'true'); }

      // Build product handle from URL (matches drop calendar slug pattern)
      var productHandle = window.location.pathname.split('/products/')[1] || 'unknown';
      productHandle = productHandle.split('?')[0].split('#')[0].split('/')[0];
      var tag = 'waitlist-' + productHandle;

      // Use the Shopify-rendered hidden form (includes authenticity_token + accepts_marketing)
      var liquidForm = document.getElementById('notice-waitlist-liquid-form');
      var emailField = document.getElementById('notice-waitlist-email-field');
      var fd;
      if (liquidForm && emailField) {
        emailField.value = email;
        fd = new FormData(liquidForm);
      } else {
        // Fallback: build FormData manually (same as drop calendar)
        fd = new FormData();
        fd.append('form_type', 'customer');
        fd.append('utf8', '\u2713');
        fd.append('contact[email]', email);
        fd.append('contact[tags]', tag);
        // Marketing consent so the requested back-in-stock email is actually sendable.
        fd.append('contact[accepts_marketing]', 'true');
      }

      fetch('/contact', {
        method: 'POST',
        credentials: 'include',
        body: fd
      })
        .then(function (r) {
          // Gate success on a real server acknowledgement (was fire-and-forget).
          if (!r.ok) throw new Error('contact POST failed: ' + r.status);

          // Fire Meta Lead ONLY on a confirmed signup (no longer fires on failure).
          try {
            document.dispatchEvent(new CustomEvent('notice:drop-notification-submit', {
              bubbles: true,
              detail: {
                email: email,
                title: productHandle,
                timestamp: new Date().toISOString(),
                source: 'pdp-waitlist'
              }
            }));
          } catch (e) {}

          form.style.display = 'none';
          confirmMsg.style.display = 'block';
        })
        .catch(function () {
          if (submitBtn) { submitBtn.disabled = false; submitBtn.removeAttribute('aria-busy'); }
          showWaitlistError('Something went wrong. Please try again.');
        });
    });

    return modal;
  }

  function initModal() {
    var _lastTrigger = null;
    // Event delegation — works even before modal exists in DOM
    document.addEventListener('click', function (e) {
      var triggerBtn = e.target.closest('[data-action="open-waitlist"]');
      if (triggerBtn) {
        _lastTrigger = triggerBtn;
        var modal = getOrCreateModal();
        // Reset form state so user can submit again after reopening
        var form = modal.querySelector('.notice-modal__form');
        var confirmMsg = modal.querySelector('.notice-modal__confirm');
        if (form) form.style.display = '';
        if (confirmMsg) confirmMsg.style.display = '';
        // Reset in-flight/error state so a prior submit doesn't leave the button stuck disabled.
        var reBtn = modal.querySelector('.notice-modal__submit');
        if (reBtn) { reBtn.disabled = false; reBtn.removeAttribute('aria-busy'); }
        var reErr = modal.querySelector('.notice-modal__error');
        if (reErr) reErr.style.display = 'none';
        modal.classList.add('is-open');
        var input = modal.querySelector('.notice-modal__input');
        if (input) {
          input.value = '';
          setTimeout(function () { input.focus(); }, 80);
        }
        return;
      }

      var closeBtn = e.target.closest('.notice-modal__close');
      if (closeBtn) {
        var modal = document.getElementById('notice-waitlist-modal');
        if (modal) modal.classList.remove('is-open');
        if (_lastTrigger) { setTimeout(function () { _lastTrigger.focus(); }, 50); }
        return;
      }

      var modal = document.getElementById('notice-waitlist-modal');
      if (modal && e.target === modal) {
        modal.classList.remove('is-open');
        if (_lastTrigger) { setTimeout(function () { _lastTrigger.focus(); }, 50); }
      }
    });

    document.addEventListener('keydown', function (e) {
      var modal = document.getElementById('notice-waitlist-modal');
      if (!modal || !modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') {
        modal.classList.remove('is-open');
        if (_lastTrigger) { setTimeout(function () { _lastTrigger.focus(); }, 50); }
        return;
      }
      if (e.key === 'Tab') {
        var f = modal.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initOverlay();
      bindVariantChange();
      initModal();
    });
  } else {
    initOverlay();
    bindVariantChange();
    initModal();
  }
})();

// GoKwik country detection and show/hide is handled by gokwik.liquid (enableIntlFlow).
// This script handles desktop where gokwikLoaded may not fire:
// polls for the button to be enabled by GoKwik's own SDK, hides it on timeout.
(function () {
  var btns = Array.prototype.slice.call(document.querySelectorAll('#gokwik-buy-now'));
  if (!btns.length) return;

  var done = false;
  var hidden = false;
  var pollTimer = null;

  function enableAll() {
    btns.forEach(function (btn) {
      btn.disabled = false;
      btn.classList.remove('disabled');
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('aria-label');
      if (hidden) btn.style.removeProperty('display');
    });
    hidden = false;
  }

  function onReady() {
    if (done) return;
    done = true;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    enableAll();
  }

  // Already enabled (SDK loaded before this ran)
  if (!btns[0].classList.contains('disabled')) { onReady(); return; }

  // Keep listening after the 4 s hide: a slow SDK must bring Buy Now back
  // instead of leaving the mobile copy hidden for the session (A05-002 B).
  window.addEventListener('gokwikLoaded', onReady);

  // Poll every 300ms for desktop where gokwikLoaded doesn't fire
  pollTimer = setInterval(function () {
    if (!btns[0].classList.contains('disabled')) onReady();
  }, 300);

  // 4s fallback — hide while the SDK is missing so ATC is clear; restored by gokwikLoaded.
  setTimeout(function () {
    if (done) return;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    hidden = true;
    btns.forEach(function (btn) { btn.style.setProperty('display', 'none', 'important'); });
  }, 4000);
})();
